// joinOnVisibility — the part of a query a JOIN's ON predicate can see, one
// rule for everything that offers names inside an ON: the VQE's structured ON
// editor (`listInScopeTables`) and the column pickers of a subquery drilled
// from inside that ON (`enclosingScopes`).
//
// A JOIN binds tighter than a comma everywhere but SQLite, so its ON sees only
// its own join tree — the comma-separated FROM item it hangs off (the last one
// before it) and the JOINs from there up to and including this one; an
// earlier comma-separated item is out of reach (PostgreSQL "invalid reference
// to FROM-clause entry", SQL Server Msg 4104, MySQL 1054, Oracle ORA-00904 —
// from the ON itself and from any subquery in it). SQLite treats the comma as
// an ordinary left-associative join, so every earlier item is visible there.
// A later JOIN's table or comma item is never offered (SQLite happens to
// accept one; the other four reject it).

import type { SqlEngine } from '../../sql-grammar/model';
import type { FromItem, QueryStructure } from '../types/queryStructure';

/**
 * The rule over one FROM list in source order — `joined[i]`: item `i` hangs
 * off the item before it by a JOIN (else it follows a comma or opens the
 * list). The indices of the items the ON of item `k` sees: its own join tree
 * up to it — from the last comma-separated item before it (every earlier
 * item on SQLite).
 */
export function onVisibleIndices(joined: readonly boolean[], k: number, engine: SqlEngine): number[] {
  let root = 0;
  if (engine !== 'sqlite') {
    for (let i = k - 1; i >= 0; i--) {
      if (!joined[i]) {
        root = i;
        break;
      }
    }
  }
  return Array.from({ length: k - root + 1 }, (_, i) => root + i);
}

/** The FROM items JOIN #`joinIndex`'s ON can name, in source order. */
export function onVisibleItems(structure: QueryStructure, joinIndex: number, engine: SqlEngine): FromItem[] {
  const joins = structure.joins ?? [];
  const join = joins[joinIndex];
  const listed = [
    ...(structure.fromItems ?? []).map((item) => ({ item, at: item.range.startOffset, joined: false })),
    ...joins.slice(0, joinIndex + 1).map((j) => ({ item: j.right, at: j.range.startOffset, joined: true })),
  ].sort((a, b) => a.at - b.at);
  if (!join) return listed.map(({ item }) => item);
  const k = listed.findIndex(({ item }) => item === join.right);
  return onVisibleIndices(
    listed.map(({ joined }) => joined),
    k,
    engine,
  ).map((i) => listed[i]!.item);
}
